import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Upload, Play, Pause, Music, Check, Sliders, X } from 'lucide-react';
import AdvancedAudioBuilder from '../../audio/AdvancedAudioBuilder';
import BreathingOrb from '../Premium/BreathingOrb';
import sonicCues from '../Premium/sonicCues';

// ─── Simplified library — 8 calm categories × 2 tracks each ───────────
// Replaces the old 29-track list. Each category holds two paired variants
// (e.g. Cinematic + Cinematic II) so the user can sample I or II before
// committing. Decision fatigue out, premium calm in.
const CATEGORIES = [
  // ─── High-energy upbeat — added Feb 2026 for gym / performance flow ─
  {
    id: 'full_throttle',
    name: 'Full Throttle',
    vibe: 'Driving, peak-hour, all in',
    upbeat: true,
    tracks: [
      { id: 'full_throttle_cinematic', name: 'Cinematic', url: '/api/audio/ambient/full_throttle_cinematic.mp3' },
      { id: 'full_throttle_edm',       name: 'EDM',       url: '/api/audio/ambient/full_throttle_edm.mp3' },
      { id: 'full_throttle_hybrid',    name: 'Hybrid',    url: '/api/audio/ambient/full_throttle_hybrid.mp3' },
    ],
  },
  // ─── Athletic Anthems · batch 1 dropped Jun 6 2026, +2 added Jun 7 2026 ─
  {
    id: 'athletic_anthems',
    name: 'Athletic Anthems',
    vibe: 'Gym · Tennis · Run · Victory',
    upbeat: true,
    tracks: [
      { id: 'atlas_gate',         name: 'Atlas Gate',     url: '/api/audio/ambient/atlas_gate.mp3' },
      { id: 'tennis_rally',       name: 'Rally Line',     url: '/api/audio/ambient/tennis_rally.mp3' },
      { id: 'running_endurance',  name: 'River Miles',    url: '/api/audio/ambient/running_endurance.mp3' },
      { id: 'victory_pulse',      name: 'Victory Pulse',  url: '/api/audio/ambient/victory_pulse.mp3' },
      { id: 'magnetic_pulse',     name: 'Magnetic Pulse', url: '/api/audio/ambient/magnetic_pulse.mp3' },
      { id: 'morning_ignite',     name: 'Morning Ignite', url: '/api/audio/ambient/morning_ignite.mp3' },
    ],
  },
  // ─── Sovereign · batch 2 dropped Jun 7 2026 (executive presence) ──────
  // Two tracks: warm-luminous dawn energy (Golden Rise) and low-volume
  // executive focus (Executive Calm). upbeat=false → uses the gold (not
  // ember) accent in the picker.
  {
    id: 'sovereign',
    name: 'Sovereign',
    vibe: 'Executive presence, quiet authority',
    tracks: [
      { id: 'golden_rise',     name: 'Golden Rise',    url: '/api/audio/ambient/golden_rise.mp3' },
      { id: 'executive_calm',  name: 'Executive Calm', url: '/api/audio/ambient/executive_calm.mp3' },
    ],
  },
  {
    id: 'cinematic',
    name: 'Cinematic',
    vibe: 'Wide, filmic, expansive',
    tracks: [
      { id: 'cinematic',     name: 'I',  url: '/api/audio/ambient/cinematic.mp3' },
      { id: 'cinematic_alt', name: 'II', url: '/api/audio/ambient/cinematic_alt.mp3' },
    ],
  },
  {
    id: 'arrival',
    name: 'Arrival',
    vibe: 'Soft entrance, gentle landing',
    tracks: [
      { id: 'arrival',     name: 'I',  url: '/api/audio/ambient/arrival.mp3' },
      { id: 'arrival_alt', name: 'II', url: '/api/audio/ambient/arrival_alt.mp3' },
    ],
  },
  {
    id: 'hero',
    name: 'Hero',
    vibe: 'Quiet trailer, lifted',
    tracks: [
      { id: 'trailer_calm',     name: 'I',  url: '/api/audio/ambient/trailer_calm.mp3' },
      { id: 'trailer_calm_alt', name: 'II', url: '/api/audio/ambient/trailer_calm_alt.mp3' },
    ],
  },
  {
    id: 'lofi',
    name: 'Lo-Fi',
    vibe: 'Warm, soft, lived-in',
    tracks: [
      { id: 'lofi',     name: 'I',  url: '/api/audio/ambient/lofi.mp3' },
      { id: 'lofi_alt', name: 'II', url: '/api/audio/ambient/lofi_alt.mp3' },
    ],
  },
  {
    id: 'flow_pulse',
    name: 'Flow Pulse',
    vibe: 'Forward motion, steady',
    tracks: [
      { id: 'flow_pulse',     name: 'I',  url: '/api/audio/ambient/flow_pulse.mp3' },
      { id: 'flow_pulse_alt', name: 'II', url: '/api/audio/ambient/flow_pulse_alt.mp3' },
    ],
  },
  {
    id: 'deep_minimal',
    name: 'Deep Minimal',
    vibe: 'Spacious, low, anchoring',
    tracks: [
      { id: 'deep_minimal',     name: 'I',  url: '/api/audio/ambient/deep_minimal.mp3' },
      { id: 'deep_minimal_alt', name: 'II', url: '/api/audio/ambient/deep_minimal_alt.mp3' },
    ],
  },
  {
    id: 'meditation',
    name: 'Meditation',
    vibe: 'Hush, breath, stillness',
    tracks: [
      { id: 'meditation',     name: 'I',  url: '/api/audio/ambient/meditation.mp3' },
      { id: 'meditation_alt', name: 'II', url: '/api/audio/ambient/meditation_alt.mp3' },
    ],
  },
  {
    id: 'nature',
    name: 'Nature',
    vibe: 'Forest air, alive',
    tracks: [
      { id: 'forest',       name: 'I',   url: '/api/audio/ambient/forest.mp3' },
      { id: 'forest_2',     name: 'II',  url: '/api/audio/ambient/forest_2.mp3' },
      { id: 'forest_alive', name: 'III', url: '/api/audio/ambient/forest_alive.mp3' },
    ],
  },
  // ─── Added May 2026 — second Dropbox pack (16 tracks across 7 themes) ─
  {
    id: 'rain',
    name: 'Rain',
    vibe: 'Steady downpour, washes through',
    tracks: [
      { id: 'rain',   name: 'I',  url: '/api/audio/ambient/rain.mp3' },
      { id: 'rain_2', name: 'II', url: '/api/audio/ambient/rain_2.mp3' },
    ],
  },
  {
    id: 'ocean',
    name: 'Ocean',
    vibe: 'Tide rolling in, deep breath',
    tracks: [
      { id: 'ocean',       name: 'I',   url: '/api/audio/ambient/ocean.mp3' },
      { id: 'ocean_2',     name: 'II',  url: '/api/audio/ambient/ocean_2.mp3' },
      { id: 'ocean_pulse', name: 'III', url: '/api/audio/ambient/ocean_pulse.mp3' },
    ],
  },
  {
    id: 'flowing_water',
    name: 'Flowing Water',
    vibe: 'River movement, soft current',
    tracks: [
      { id: 'flowing_water',   name: 'I',  url: '/api/audio/ambient/flowing_water.mp3' },
      { id: 'flowing_water_2', name: 'II', url: '/api/audio/ambient/flowing_water_2.mp3' },
    ],
  },
  {
    id: 'healing',
    name: 'Healing',
    vibe: 'Warm presence, restorative',
    tracks: [
      { id: 'healing',   name: 'I',  url: '/api/audio/ambient/healing.mp3' },
      { id: 'healing_2', name: 'II', url: '/api/audio/ambient/healing_2.mp3' },
    ],
  },
  {
    id: 'nocturne',
    name: 'Nocturne',
    vibe: 'Late night, low piano',
    tracks: [
      { id: 'nocturne',   name: 'I',  url: '/api/audio/ambient/nocturne.mp3' },
      { id: 'nocturne_2', name: 'II', url: '/api/audio/ambient/nocturne_2.mp3' },
    ],
  },
  {
    id: 'deep_drone',
    name: 'Deep Drone',
    vibe: 'Low resonance, anchoring hum',
    tracks: [
      { id: 'deep_drone',   name: 'I',  url: '/api/audio/ambient/deep_drone.mp3' },
      { id: 'deep_drone_2', name: 'II', url: '/api/audio/ambient/deep_drone_2.mp3' },
    ],
  },
  {
    id: 'pink_noise',
    name: 'Pink Noise',
    vibe: 'Even hush, wraps everything',
    tracks: [
      { id: 'pink_noise',   name: 'I',  url: '/api/audio/ambient/pink_noise.mp3' },
      { id: 'pink_noise_2', name: 'II', url: '/api/audio/ambient/pink_noise_2.mp3' },
    ],
  },
];

// ─── Vibe filter — Reclaim Premium Suite (D3.51, Jun 2026) ────────────
// Five lozenge chips above the picker that filter the 19-category list
// down to the current intent. Default = "All" so the full library is
// always one tap away. The mapping intentionally lives here (not on each
// category) so the picker library data stays a clean source of truth.
const VIBE_FILTERS = [
  { id: 'all',    label: 'All' },
  { id: 'calm',   label: 'Calm' },
  { id: 'focus',  label: 'Focus' },
  { id: 'upbeat', label: 'Upbeat' },
  { id: 'nature', label: 'Nature' },
];

const CATEGORY_VIBE = {
  // Upbeat / driving
  full_throttle:    'upbeat',
  athletic_anthems: 'upbeat',
  // Focus / forward / cinematic
  sovereign:        'focus',
  flow_pulse:       'focus',
  deep_drone:       'focus',
  pink_noise:       'focus',
  cinematic:        'focus',
  hero:             'focus',
  // Calm / restorative
  arrival:          'calm',
  lofi:             'calm',
  deep_minimal:     'calm',
  meditation:       'calm',
  healing:          'calm',
  nocturne:         'calm',
  // Nature / elemental
  nature:           'nature',
  rain:             'nature',
  ocean:            'nature',
  flowing_water:    'nature',
};

// Sample length (seconds) for the in-line preview on tap.
const SAMPLE_SECONDS = 8;

const MusicSelection = ({ archetype, onSelect, onBack, accent = 'gold' }) => {
  // accent: 'gold' (Private Suite premium) or 'purple' (legacy Reality States).
  const PALETTE = accent === 'purple'
    ? {
        border:       'border-purple-400/35',
        bg:           'bg-purple-500/10',
        variantBorder:'border border-purple-400/50',
        variantBg:    'bg-purple-500/20',
        sampleBg:     'rgba(168,85,247,0.30)',
        sampleRing:   'rgba(168,85,247,0.85)',
        check:        'bg-purple-500',
        chosenText:   'text-purple-300',
        upload:       'border-purple-500 bg-purple-500/10',
        uploadCheck:  'bg-purple-500',
        cta:          'from-purple-500 to-violet-500 shadow-purple-500/30',
      }
    : {
        border:       'border-amber-300/35',
        bg:           'bg-amber-300/10',
        variantBorder:'border border-amber-300/45',
        variantBg:    'bg-amber-300/15',
        sampleBg:     'rgba(241,210,156,0.25)',
        sampleRing:   'rgba(241,210,156,0.85)',
        check:        'bg-amber-300',
        chosenText:   'text-amber-200',
        upload:       'border-amber-300 bg-amber-300/10',
        uploadCheck:  'bg-amber-300',
        cta:          'from-amber-300 to-amber-500 shadow-amber-300/30',
      };
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [samplingId, setSamplingId] = useState(null); // track currently sampling
  const [sampleProgress, setSampleProgress] = useState(0); // 0..1
  const [activeVibe, setActiveVibe] = useState('all');
  const audioRef = useRef(null);
  const sampleTimerRef = useRef(null);
  const sampleTickRef = useRef(null);

  // Filtered category list — memoised so motion staggers stay stable.
  const visibleCategories = useMemo(() => {
    if (activeVibe === 'all') return CATEGORIES;
    return CATEGORIES.filter((c) => CATEGORY_VIBE[c.id] === activeVibe);
  }, [activeVibe]);

  // ─── Advanced Audio Builder (bottom sheet) ──────
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [library, setLibrary] = useState(null);
  const [advancedSel, setAdvancedSel] = useState({ mode: "soft", musicId: null, binauralId: "alpha" });

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${API}/api/unified-audio/library`).then((r) => r.json()).then(setLibrary).catch(() => {});
  }, []);

  // When the user picks a track via the advanced builder, mirror it into selectedTrack
  useEffect(() => {
    if (!advancedSel.musicId || !library) return;
    const tr = library.tracks.find((t) => t.id === advancedSel.musicId);
    if (tr) setSelectedTrack({ ...tr, fromAdvanced: true });
  }, [advancedSel.musicId, library]);

  // Hard teardown — sample audio + timers — on unmount + page hide.
  useEffect(() => {
    const cleanup = () => {
      const a = audioRef.current;
      if (a) { a.pause(); a.src = ""; a.load && a.load(); }
      if (sampleTimerRef.current) clearTimeout(sampleTimerRef.current);
      if (sampleTickRef.current) clearInterval(sampleTickRef.current);
      sampleTimerRef.current = null;
      sampleTickRef.current = null;
    };
    window.addEventListener("pagehide", cleanup);
    return () => { window.removeEventListener("pagehide", cleanup); cleanup(); };
  }, []);

  // Dismiss the picker with the Escape key — accessibility + matches the
  // BinauralPicker behaviour. Without this the user gets stuck inside the
  // fullscreen overlay if they navigated here by accident.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && onBack) onBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const stopSample = () => {
    const a = audioRef.current;
    if (a) a.pause();
    if (sampleTimerRef.current) clearTimeout(sampleTimerRef.current);
    if (sampleTickRef.current) clearInterval(sampleTickRef.current);
    sampleTimerRef.current = null;
    sampleTickRef.current = null;
    setSamplingId(null);
    setSampleProgress(0);
  };

  // Sample a track for SAMPLE_SECONDS seconds. Tapping again on the same
  // track stops it; tapping a different track swaps cleanly.
  const handleSample = (track) => {
    if (samplingId === track.id) {
      stopSample();
      return;
    }
    stopSample();
    const a = audioRef.current;
    if (!a) return;
    a.src = track.url;
    a.currentTime = 0;
    a.play().then(() => {
      setSamplingId(track.id);
      setSampleProgress(0);
      const start = Date.now();
      sampleTickRef.current = setInterval(() => {
        const p = Math.min(1, (Date.now() - start) / (SAMPLE_SECONDS * 1000));
        setSampleProgress(p);
      }, 100);
      sampleTimerRef.current = setTimeout(() => stopSample(), SAMPLE_SECONDS * 1000);
    }).catch(() => stopSample());
  };

  const handleSelect = (track, categoryName) => {
    setSelectedTrack({ ...track, displayName: `${categoryName} ${track.name}` });
  };

  const handleContinue = () => {
    stopSample();
    if (selectedTrack) onSelect(selectedTrack);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setSelectedTrack({
        id: 'uploaded',
        name: file.name.replace(/\.[^/.]+$/, ''),
        displayName: file.name.replace(/\.[^/.]+$/, ''),
        url,
        isUploaded: true,
      });
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col z-50 overflow-hidden" style={{
      background: "radial-gradient(ellipse at 50% 0%, #18213A 0%, #0a1224 45%, #030610 100%)",
    }}>
      <audio ref={audioRef} />

      {/* Signature Moment #1 — Breathing Orb behind the entire picker.
          Subtle gold pulse on alpha-band timing so the choice itself
          feels like a meditation. */}
      <BreathingOrb brainState="alpha" tone="gold" size={820} testid="music-picker-orb" />

      <div className="flex-1 overflow-y-auto px-6 py-8 relative z-10">
        {/* Back + Close — both dismiss the picker. Close is the obvious
            top-right affordance; Back keeps the existing left-aligned flow. */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            onClick={onBack}
            className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors"
            data-testid="music-back-btn"
          >
            <ChevronLeft size={20} />
            <span className="text-sm">Back</span>
          </motion.button>
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            onClick={onBack}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
            data-testid="music-picker-close"
            aria-label="Close music picker"
          >
            <X size={18} />
          </motion.button>
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="text-[10px] uppercase tracking-[0.5em] text-amber-300/55 mb-3">
            Choose your sound
          </p>
          <h1
            className="text-4xl font-light text-white mb-3 tracking-wide"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Sample · then choose
          </h1>
          <p className="text-white/45 text-sm italic">
            Tap any variant to hear {SAMPLE_SECONDS} seconds
          </p>
        </motion.div>

        {/* Upload */}
        <motion.label
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`w-full p-4 rounded-2xl border border-dashed cursor-pointer flex items-center gap-3 mb-6 transition-colors ${
            selectedTrack?.isUploaded ? PALETTE.upload : 'border-white/15 hover:border-white/30'
          }`}
          data-testid="music-upload-row"
        >
          <input type="file" accept="audio/*" onChange={handleUpload} className="hidden" />
          <div className="w-10 h-10 rounded-xl bg-white/8 flex items-center justify-center">
            <Upload size={16} className="text-white/55" />
          </div>
          <div className="flex-1">
            <p className="text-white/85 text-sm">Upload your own</p>
            <p className="text-white/35 text-[11px]">MP3 · WAV · M4A</p>
          </div>
          {selectedTrack?.isUploaded && (
            <div className={`w-7 h-7 rounded-full ${PALETTE.uploadCheck} flex items-center justify-center`}>
              <Check size={13} className="text-[#1f3a5c]" />
            </div>
          )}
        </motion.label>

        {/* Vibe filter — gilded lozenge chip row, scrollable on small viewports */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-2 mb-5 overflow-x-auto scrollbar-hide"
          role="tablist"
          aria-label="Filter music by vibe"
          data-testid="music-vibe-filter"
          style={{ scrollbarWidth: 'none' }}
        >
          {VIBE_FILTERS.map((v) => {
            const active = activeVibe === v.id;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  if (active) return;
                  sonicCues.tap();
                  setActiveVibe(v.id);
                }}
                className="rec-chip flex-shrink-0"
                data-active={active}
                data-testid={`music-vibe-${v.id}`}
                style={{ padding: '0.5rem 0.95rem', fontSize: '0.58rem' }}
              >
                {v.label}
              </button>
            );
          })}
        </motion.div>

        {/* Categories — filtered by vibe */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="space-y-3 mb-8"
        >
          <AnimatePresence mode="popLayout">
            {visibleCategories.length === 0 ? (
              <motion.p
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center italic py-8"
                style={{
                  color: 'rgba(243, 237, 221, 0.45)',
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '1.05rem',
                }}
                data-testid="music-vibe-empty"
              >
                Nothing in this vibe yet.
              </motion.p>
            ) : (
              visibleCategories.map((cat, ci) => (
                <CategoryCard
                  key={cat.id}
                  cat={cat}
                  ci={ci}
                  samplingId={samplingId}
                  sampleProgress={sampleProgress}
                  selectedTrackId={selectedTrack?.id}
                  onSample={handleSample}
                  onSelect={handleSelect}
                  palette={PALETTE}
                />
              ))
            )}
          </AnimatePresence>
        </motion.div>

        {/* Advanced builder — power user only */}
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          onClick={() => setShowAdvanced(true)}
          className="w-full mb-8 rounded-2xl py-2.5 flex items-center justify-center gap-2 transition-all"
          style={{
            background: "rgba(203,161,83,0.06)",
            border: "1px dashed rgba(203,161,83,0.25)",
          }}
          data-testid="open-advanced-builder"
        >
          <Sliders size={12} strokeWidth={1.4} style={{ color: "rgba(203,161,83,0.7)" }} />
          <span className="text-[11px] font-light tracking-wide" style={{ color: "rgba(203,161,83,0.7)" }}>
            Advanced — full library + binaural
          </span>
        </motion.button>
      </div>

      {/* Continue */}
      <div className="flex-shrink-0 p-6 pb-24 bg-gradient-to-t from-[#03060f] via-[#03060f]/95 to-transparent">
        <motion.button
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          whileHover={selectedTrack ? { scale: 1.005 } : undefined}
          whileTap={selectedTrack ? { scale: 0.992 } : undefined}
          onClick={handleContinue}
          disabled={!selectedTrack}
          className="w-full py-4 rounded-2xl relative overflow-hidden"
          style={{
            background: selectedTrack
              ? "linear-gradient(160deg, rgba(215, 187, 122, 0.32) 0%, rgba(168, 138, 76, 0.10) 100%)"
              : "rgba(255, 255, 255, 0.04)",
            border: `1px solid ${selectedTrack ? "rgba(215, 187, 122, 0.55)" : "rgba(255, 255, 255, 0.08)"}`,
            color: selectedTrack ? "#F3EDDD" : "rgba(255, 255, 255, 0.32)",
            cursor: selectedTrack ? "pointer" : "not-allowed",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.7rem",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            boxShadow: selectedTrack
              ? "0 0 24px rgba(215, 187, 122, 0.18), 0 0 0 1px rgba(215, 187, 122, 0.18) inset"
              : "none",
          }}
          data-testid="music-continue-btn"
        >
          {selectedTrack
            ? `Continue with ${selectedTrack.displayName || selectedTrack.name}`
            : 'Choose a sound'}
        </motion.button>
      </div>

      <AdvancedAudioBuilder
        open={showAdvanced}
        onClose={() => setShowAdvanced(false)}
        selection={advancedSel}
        onChange={setAdvancedSel}
        library={library}
      />
    </div>
  );
};

// ─── One category — name + vibe + 2 sample-able variant pills ─────────
const CategoryCard = ({ cat, ci, samplingId, sampleProgress, selectedTrackId, onSample, onSelect, palette }) => {
  const isCategoryActive = cat.tracks.some((t) => t.id === selectedTrackId);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + ci * 0.05, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      className={`rec-surface px-5 py-4 transition-all ${
        isCategoryActive
          ? (cat.upbeat ? 'ring-1' : 'ring-1')
          : ''
      }`}
      style={isCategoryActive ? {
        borderColor: cat.upbeat ? "rgba(255, 77, 41, 0.55)" : "rgba(215, 187, 122, 0.55)",
        boxShadow: cat.upbeat
          ? "0 0 26px rgba(255, 77, 41, 0.22), 0 0 0 1px rgba(255, 77, 41, 0.32) inset"
          : "0 0 26px rgba(215, 187, 122, 0.16), 0 0 0 1px rgba(215, 187, 122, 0.32) inset",
      } : undefined}
      data-testid={`music-category-${cat.id}`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium tracking-wide flex items-center gap-2" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: '1.1rem' }}>
            {cat.name}
            {cat.upbeat && (
              <span
                className="px-1.5 py-0.5 rounded-full uppercase"
                style={{
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.46rem",
                  letterSpacing: "0.22em",
                  background: "rgba(232, 122, 80, 0.16)",
                  border: "1px solid rgba(232, 122, 80, 0.42)",
                  color: "#F4C9B8",
                }}
                data-testid="music-category-upbeat-pill"
              >
                Upbeat
              </span>
            )}
          </p>
          <p className="text-white/40 text-[11px] italic">{cat.vibe}</p>
        </div>
      </div>
      <div className="flex gap-2">
        {cat.tracks.map((track) => (
          <VariantPill
            key={track.id}
            track={track}
            categoryName={cat.name}
            sampling={samplingId === track.id}
            sampleProgress={samplingId === track.id ? sampleProgress : 0}
            selected={selectedTrackId === track.id}
            onSample={() => onSample(track)}
            onSelect={() => onSelect(track, cat.name)}
            palette={palette}
          />
        ))}
      </div>
    </motion.div>
  );
};

// ─── One variant pill — Sample button (with progress ring) + Choose ──
const VariantPill = ({ track, categoryName, sampling, sampleProgress, selected, onSample, onSelect, palette }) => (
  <div
    className={`flex-1 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 transition-all ${
      selected
        ? `${palette.variantBg} ${palette.variantBorder}`
        : 'bg-white/[0.04] border border-white/[0.08]'
    }`}
    data-testid={`music-variant-${track.id}`}
  >
    {/* Sample-with-progress button */}
    <button
      onClick={onSample}
      className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
      style={{
        background: sampling ? palette.sampleBg : "rgba(255,255,255,0.08)",
      }}
      data-testid={`music-sample-${track.id}`}
      aria-label={sampling ? "Stop sample" : "Sample track"}
    >
      {sampling && (
        <svg
          className="absolute inset-0 -rotate-90"
          viewBox="0 0 36 36"
          width="36"
          height="36"
          aria-hidden
        >
          <circle
            cx="18" cy="18" r="16"
            fill="none"
            stroke={palette.sampleRing}
            strokeWidth="2"
            strokeDasharray={`${sampleProgress * 100.5} 100.5`}
            strokeLinecap="round"
          />
        </svg>
      )}
      {sampling
        ? <Pause size={13} className="text-white relative z-10" />
        : <Play size={13} className="text-white/85 ml-0.5 relative z-10" />}
    </button>
    <button
      onClick={onSelect}
      className="flex-1 text-left text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white transition-colors"
      data-testid={`music-choose-${track.id}`}
      aria-label={`Choose ${categoryName} ${track.name}`}
    >
      {selected ? <span className={palette.chosenText}>Chosen · {track.name}</span> : <>Variant {track.name}</>}
    </button>
    {selected && (
      <div className={`w-5 h-5 rounded-full ${palette.check} flex items-center justify-center flex-shrink-0`}>
        <Check size={10} className="text-[#1f3a5c]" />
      </div>
    )}
  </div>
);

export default MusicSelection;
